// PR-R2 — set each site's billing mode (per-trip flat or per-m3) and rate. You
// pick the mode and the rate; revenue computes via trip-math (per-trip = flat,
// no ×m3). Saving recomputes that site's trips; a closed month asks to confirm.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Coins, AlertTriangle, Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Contract {
  id: string; site: string | null; mode: 'per_trip' | 'per_m3'; rate: number;
  targetQty: number; driverRate: number; investorRate: number | null; trips: number;
}

export function BillingRatesSheet({ scope, onClose }: { scope: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['billing-contracts', scope],
    queryFn: async () => (await apiClient.get<{ items: Contract[] }>('/api/v1/project-reports/contracts', { params: { scope } })).data.items,
  });

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Coins className="h-5 w-5" /> Billing rates</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-3" data-billing-rates>
          <p className="text-xs text-muted-foreground">
            Pick how each site bills and set the rate. Per-trip = a flat rate per trip. Per-m³ = rate × m³ delivered. Saving recomputes that site's trips through trip-math.
          </p>
          {isLoading || !data ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : data.length === 0 ? (
            <div className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">No active contracts.</div>
          ) : (
            data.map((c) => <ContractRow key={c.id} c={c} onSaved={() => { refetch(); qc.invalidateQueries({ queryKey: ['vehicle-pnl'] }); qc.invalidateQueries({ queryKey: ['vehicle-detail'] }); }} />)
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ContractRow({ c, onSaved }: { c: Contract; onSaved: () => void }) {
  const [mode, setMode] = useState<'per_trip' | 'per_m3'>(c.mode);
  const [rate, setRate] = useState(String(c.rate));
  const [target, setTarget] = useState(String(c.targetQty || 28));
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<null | { lockedCount: number; months: string[] }>(null);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const rateNum = Number(rate) || 0;
  // For per-trip the client rate IS the rate; for per-m3 the at-target client = rate × target.
  const clientAtTarget = mode === 'per_trip' ? rateNum : rateNum * (Number(target) || 0);
  const belowInvestor = c.investorRate != null && clientAtTarget < c.investorRate;

  async function save(confirmLocked = false) {
    setBusy(true); setErr(null);
    try {
      const res = await apiClient.post<{ needsConfirm?: boolean; lockedCount?: number; lockedMonths?: string[]; recomputed?: number }>(
        '/api/v1/trips/contract-billing',
        { siteContractId: c.id, mode, clientRate: rateNum, perM3TargetQty: mode === 'per_m3' ? Number(target) : undefined, recompute: true, confirmLocked },
      );
      if (res.data?.needsConfirm) { setConfirm({ lockedCount: res.data.lockedCount ?? 0, months: res.data.lockedMonths ?? [] }); return; }
      setConfirm(null); setDone(true); onSaved();
      setTimeout(() => setDone(false), 2500);
    } catch (e) { setErr(extractErrorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="font-medium">{c.site ?? 'Site'}</div>
        <div className="text-xs text-muted-foreground">{c.trips} trips{c.investorRate != null ? ` · investor ${c.investorRate.toLocaleString()}` : ''}</div>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <label className="text-[11px] text-muted-foreground">Mode</label>
          <select value={mode} onChange={(e) => setMode(e.target.value as 'per_trip' | 'per_m3')} className="h-9 rounded-md border border-input bg-background px-2 text-sm block">
            <option value="per_trip">Per trip (flat)</option>
            <option value="per_m3">Per m³</option>
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] text-muted-foreground">{mode === 'per_trip' ? 'SAR / trip' : 'SAR / m³'}</label>
          <Input type="number" min="0" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className="h-9 text-sm w-28" />
        </div>
        {mode === 'per_m3' && (
          <div className="space-y-1">
            <label className="text-[11px] text-muted-foreground">Tanker m³</label>
            <Input type="number" min="1" step="0.1" value={target} onChange={(e) => setTarget(e.target.value)} className="h-9 text-sm w-20" />
          </div>
        )}
        <Button size="sm" onClick={() => save()} disabled={busy || rateNum <= 0} className="gap-1 h-9">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : done ? <Check className="h-3.5 w-3.5" /> : null}
          {done ? 'Saved' : 'Save + recompute'}
        </Button>
      </div>
      <div className="text-[11px] text-muted-foreground">
        Revenue/trip becomes <span className="font-mono">{(mode === 'per_trip' ? rateNum : clientAtTarget).toLocaleString()}</span>{mode === 'per_m3' ? ` (at ${target} m³)` : ''}.
      </div>
      {belowInvestor && (
        <div className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <AlertTriangle className="h-3.5 w-3.5" /> Below the investor rate ({c.investorRate?.toLocaleString()}) — the company would lose per trip. Lower the investor rate too.
        </div>
      )}
      {confirm && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-[11px] space-y-1.5">
          <div className="text-amber-700 dark:text-amber-400">{confirm.lockedCount} trip(s) are in a closed month ({confirm.months.join(', ')}).</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
            <Button size="sm" onClick={() => save(true)} disabled={busy}>Recompute anyway</Button>
          </div>
        </div>
      )}
      {err && <div className="text-[11px] text-rose-600 dark:text-rose-400">{err}</div>}
    </div>
  );
}
